import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import { VISITOR_INVITATION_MODEL} from "../Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import {USER_TYPE_NAME_MODEL} from "../../../../../enums/userTypeEnum";
import {CreateInvitation} from "../../../../../reduxToolkit/Invitation/InvitationApi";
import {GetTolerances} from "../../../../../reduxToolkit/SystemConfig/SystemConfigApi";
import {toast} from "react-toastify";

import {errorMsg, userListToLabelEmailPhoneValue} from "../../../../../Apis/functions";
import {
    getWorkStations
} from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import routes from "../../../../../Routes/routes";
import {useLocation, useNavigate} from "react-router-dom";
import {GetRequestAccessById} from "../../../../../reduxToolkit/RequestAccess/RequestAccessApi";
import {GetAllVisitorTypes} from "../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {PersonChipBlack} from "../../../../../components/buttons/IconButtons";
import {ErrorCode} from "../../../../../Apis/ErrorCode";
import {NONE_VISITOR_TYPE} from "../../Type/Model/Model";

/**
 * @fileoverview Business logic hook for handling visitor invitation creation in the Visitor module.
 * Responsible for fetching required data, managing state, handling form changes, validation,
 * and submission to the backend.
 *
 * @module CreateVisitorInvitationApp
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * Custom React hook that manages the state and behavior for creating a new visitor invitation.
 * It includes data fetching, validation, field management, and dispatching the invitation creation API.
 */


export const CreateVisitorInvitationApp =()=> {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    let { state } = location;
    const requestAccessId = state?.requestAccessId;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice);
    // A guard carries no permissions, its user type requests access for visitors
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const userData = useSelector((state) => state?.authenticatioauthennSlice?.user.data);
    const [item, setItem] = useState({...VISITOR_INVITATION_MODEL});
    const [modified, setModified] = useState(false);
    const [addShow, setAddShow] = useState(false);
    const [cleanChipSearchHost, setCleanChipSearchHost] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [selectedHost, setSelectedHost] = useState([]);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [emptyFields, setEmptyFields] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [isValidStartDate, setIsValidStartDate] = useState(true);
    const [isValidEndDate, setIsValidEndDate] = useState(true);
    const [defaultTolerance, setDefaultTolerance] = useState(null);
    const getAllVisitorTypes = useSelector((state) => state?.EmployeeEventsSlice?.getAllVisitorTypes);
    // Visitor type applied when the invitation does not require documentation validation.
    const noneVisitorTypeId = useMemo(
        () => getAllVisitorTypes?.find(e => e.name === NONE_VISITOR_TYPE)?.id ?? null,
        [getAllVisitorTypes]
    );

    /**
     * Keeps the visitor type in sync with the documentation validation switch:
     * when the validation is not required the NONE type is applied, and when it
     * is required the NONE type is cleared so the user has to pick one.
     */
    useEffect(() => {
        setItem(prev => ({
            ...prev,
            visitorType: prev.documentationValidated
                ? (prev.visitorType === noneVisitorTypeId ? null : prev.visitorType)
                : noneVisitorTypeId
        }));
    }, [item?.documentationValidated, noneVisitorTypeId]);

    useEffect(()=>{
        if(requestAccessId)
            dispatch(GetRequestAccessById(requestAccessId)).then(({payload:{data:{ data, success }}})=>{
                if(success){
                    let hostData = item.buildDataToHost(data);
                    let visitorData = item.buildDataToVisitor(data);
                    setSelectedHost(userListToLabelEmailPhoneValue([hostData], 'id', false, PersonChipBlack));
                    setSelectedList(userListToLabelEmailPhoneValue([visitorData], 'id', true, PersonChipBlack));
                    // Keep the preloaded system default tolerance; the request access
                    // payload does not carry one and would otherwise reset it to null.
                    setItem(prev => ({
                        ...item.setFromRequestAccess({ requestAccessId: requestAccessId , ...data}),
                        accessToleranceMin: prev.accessToleranceMin
                    }));
                }
            });
    },[requestAccessId])

   useEffect(()=>{
        dispatch(GetAllVisitorTypes());
        dispatch(getWorkStations());
        // Preload the access tolerance field with the system default on first load.
        // The user may still override it; the rest of the flow stays the same.
        dispatch(GetTolerances()).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                const def = data?.accessToleranceJustOnceMin ?? null;
                setDefaultTolerance(def);
                setItem(prev => ({
                    ...prev,
                    accessToleranceMin: def
                }));
            }
        });
    },[])

    /**
     * Validates the start date of the item.
     *
     * If a start date exists and is earlier than the current date/time,
     * the start date is considered invalid. Otherwise, it is valid.
     *
     * This effect runs whenever `item.startDate` changes.
     */
    useEffect(()=>{
      if(item?.startDate && Date.now() > new Date(item?.startDate))
          setIsValidStartDate(false);
      else
          setIsValidStartDate(true);
    },[item?.startDate]);

    /**
     * Validates the end date based on the start date.
     *
     * When both start and end dates exist:
     *  - The end date must be at least 30 minutes after the start date.
     *  - If it does not meet this rule, the end date is marked as invalid.
     *
     * This effect runs whenever `item.startDate` or `item.endDate` changes.
     */
    useEffect(()=>{
        if(item?.startDate && item?.endDate){
            const start = new Date(item?.startDate).getTime();
            const end = new Date(item?.endDate).getTime();
            if (end < start + 30 * 60 * 1000) {
                setIsValidEndDate(false);
            }else{
                setIsValidEndDate(true)
            }
        }
    },[item?.startDate, item?.endDate]);

    /**
     * Resets the form and clears selected data.
     */
    const handleClear = () => {
        if(requestAccessId){
            setItem({...item.clearFromRequestAccess(), accessToleranceMin: defaultTolerance, visitorType: noneVisitorTypeId});
            setEmptyFields(false);
            return;
        }
        setItem({...VISITOR_INVITATION_MODEL, accessToleranceMin: defaultTolerance, visitorType: noneVisitorTypeId});
        setSelectedList([])
        setSelectedHost([])
        setEmptyFields(false);
        setIsValidStartDate(true);
        setIsValidEndDate(true);
    };
    /**
     * Validates and sends the visitor invitation data to the backend.
     * Handles required fields and time constraints (start and end must be at least 30 mins apart).
     */
    const handleCreate = async () => {
        if (!item?.isValid() || !item.isValidFields(selectedList) || !isValidStartDate || !isValidEndDate) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);
        const aux = {
            ...item,
            visitors: selectedList?.map(i => i.id) || [],
            hostId: selectedHost[0]?.id || userData?.id,
            visitorType : {
                id: item?.documentationValidated ? item?.visitorType : noneVisitorTypeId
            }
        };

        const body = aux.toRegister();

        dispatch(CreateInvitation(body))
            .then(({ payload: { data: { success, code }}}) => {
                if (success) {
                    handleClear();
                    toast.success(t('has_been_created_successfully'));
                    goBack();
                } else {
                    if(code === ErrorCode.START_DATE_IN_PAST){
                        setIsValidStartDate(false);
                        return;
                    }
                    errorMsg(code);
                }
            })
        setEmptyFields(false);
    };

    const goBack = () => {
        if(requestAccessId) navigate(state.backUrl, { state: {...state } } )
        else navigate(routes.visitor.list);
    }

    return{
        t,
        navigate,
        DEV,
        isGuard,
        permission,
        requestAccessId,
        handleCreate,
        handleClear,
        handleChange,
        disabled,
        item,
        emptyFields,
        selectedList,
        setSelectedList,
        selectedHost,
        setSelectedHost,
        cleanChipSearchHost,
        setCleanChipSearchHost,
        addShow,
        setAddShow,
        modified,
        setModified,
        goBack,
        isValidStartDate,
        isValidEndDate,
        defaultTolerance
    }
}
